import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { Award, CheckCircle2, Crown, Lock } from "lucide-react";

import { Card } from "@/components/ui/card";
import { useSupabaseSession } from "@/hooks/use-supabase-session";
import { fetchProgressSummary, fetchProgressTimeline } from "@/lib/progress";

const AURA_LEVELS = [
  { key: "beyaz", label: "Beyaz", minScore: 0, accent: "slate", imageUrl: undefined as string | undefined },
  { key: "toprak", label: "Toprak", minScore: 2000, accent: "amber", imageUrl: undefined as string | undefined },
  { key: "zumrut", label: "Zümrüt", minScore: 6000, accent: "emerald", imageUrl: undefined as string | undefined },
  { key: "safir", label: "Safir", minScore: 12000, accent: "blue", imageUrl: undefined as string | undefined },
  { key: "mor_alev", label: "Mor Alev", minScore: 22000, accent: "violet", imageUrl: undefined as string | undefined },
  { key: "altin_kristal", label: "Altın Kristal", minScore: 35000, accent: "yellow", imageUrl: undefined as string | undefined },
] as const;

type RankCatalogItem = {
  key: string;
  label: string;
  minXp: number;
  imageUrl?: string;
};

const DEFAULT_RANKS: RankCatalogItem[] = [
  { key: "tohum", label: "Tohum", minXp: 0 },
  { key: "arayici", label: "Arayıcı", minXp: 100 },
  { key: "yolcu", label: "Yolcu", minXp: 250 },
  { key: "farkindalik_kivilcimi", label: "Farkındalık Kıvılcımı", minXp: 500 },
  { key: "enerji_ogrencisi", label: "Enerji Öğrencisi", minXp: 900 },
  { key: "nefes_yolcusu", label: "Nefes Yolcusu", minXp: 1400 },
  { key: "meditasyon_tasiyicisi", label: "Meditasyon Taşıyıcısı", minXp: 2000 },
  { key: "rituel_uygulayicisi", label: "Ritüel Uygulayıcısı", minXp: 2800 },
  { key: "denge_koruyucusu", label: "Denge Koruyucusu", minXp: 3700 },
  { key: "cember_yolcusu", label: "Çember Yolcusu", minXp: 4700 },
  { key: "sifa_tasiyicisi", label: "Şifa Taşıyıcısı", minXp: 5900 },
  { key: "enerji_rehberi", label: "Enerji Rehberi", minXp: 7400 },
  { key: "isik_tasiyicisi", label: "Işık Taşıyıcısı", minXp: 9200 },
  { key: "bilinc_elcisi", label: "Bilinç Elçisi", minXp: 11500 },
  { key: "ruh_mimari", label: "Ruh Mimarı", minXp: 14500 },
  { key: "frekans_ustasi", label: "Frekans Ustası", minXp: 18000 },
  { key: "cember_koruyucusu", label: "Çember Koruyucusu", minXp: 23000 },
  { key: "ruh_rehberi", label: "Ruh Rehberi", minXp: 29000 },
  { key: "kozmik_rehber", label: "Kozmik Rehber", minXp: 36000 },
  { key: "buyuk_cember_bilgesi", label: "Büyük Çember Bilgesi", minXp: 45000 },
];

const BADGE_GROUPS = [
  {
    key: "practice",
    title: "Pratik Rozetleri",
    categories: [
      {
        key: "diet",
        title: "Diyet",
        items: [
          { key: "diet_7", label: "Toprak Disiplini", rule: "7 gün" },
          { key: "diet_21", label: "Beslenme Ustası", rule: "21 gün" },
          { key: "diet_40", label: "Arınma Koruyucusu", rule: "40 gün" },
        ],
      },
      {
        key: "meditation",
        title: "Meditasyon",
        items: [
          { key: "meditation_10", label: "Sessizlik Tohumu", rule: "10 seans" },
          { key: "meditation_30", label: "İçsel Denge", rule: "30 seans" },
          { key: "meditation_75", label: "Meditasyon Bilgesi", rule: "75 seans" },
        ],
      },
      {
        key: "breath",
        title: "Nefes",
        items: [
          { key: "breath_10", label: "Nefes Kıvılcımı", rule: "10 seans" },
          { key: "breath_30", label: "Yaşam Nefesi", rule: "30 seans" },
          { key: "breath_75", label: "Nefes Ustası", rule: "75 seans" },
        ],
      },
      {
        key: "ritual",
        title: "Ritüel",
        items: [
          { key: "ritual_10", label: "Ritüel Tohumu", rule: "10 seans" },
          { key: "ritual_30", label: "Ay Işığı Uygulayıcısı", rule: "30 seans" },
          { key: "ritual_75", label: "Kutsal Döngü Bekçisi", rule: "75 seans" },
        ],
      },
      {
        key: "task",
        title: "Çember",
        items: [
          { key: "task_20", label: "Görev Yolcusu", rule: "20 görev" },
          { key: "task_60", label: "Çember Emektarı", rule: "60 görev" },
          { key: "task_120", label: "Çember Sütunu", rule: "120 görev" },
        ],
      },
    ],
  },
  {
    key: "community",
    title: "Topluluk",
    categories: [
      {
        key: "social",
        title: "Paylaşım & Enerji",
        items: [
          { key: "post_10", label: "Sesini Açan", rule: "10 paylaşım" },
          { key: "post_30", label: "İlham Taşıyıcısı", rule: "30 paylaşım" },
          { key: "post_75", label: "Çember Sözcüsü", rule: "75 paylaşım" },
          { key: "energy_20", label: "Enerji Kıvılcımı", rule: "20 gönderim" },
          { key: "energy_75", label: "Işık Destekçisi", rule: "75 gönderim" },
          { key: "energy_150", label: "Enerji Akışı Ustası", rule: "150 gönderim" },
          { key: "healing_20", label: "Şefkat Tohumu", rule: "20 dilek" },
          { key: "healing_75", label: "Şifa Kalbi", rule: "75 dilek" },
          { key: "healing_150", label: "Şifa Elçisi", rule: "150 dilek" },
        ],
      },
    ],
  },
  {
    key: "balance",
    title: "Günlük Denge",
    categories: [
      {
        key: "daily_balance",
        title: "Denge Rozetleri",
        items: [
          { key: "balance_3", label: "Denge Kıvılcımı", rule: "3 gün" },
          { key: "balance_10", label: "Denge Taşıyıcısı", rule: "10 gün" },
          { key: "balance_25", label: "Beş Kapı Ustası", rule: "25 gün" },
        ],
      },
    ],
  },
] as const;

const auraAccent: Record<(typeof AURA_LEVELS)[number]["accent"], string> = {
  slate: "from-white via-slate-100 to-slate-200 dark:from-slate-100 dark:via-slate-200 dark:to-slate-300",
  amber: "from-amber-500 to-amber-700",
  emerald: "from-emerald-400 to-emerald-600",
  blue: "from-blue-400 to-indigo-600",
  violet: "from-violet-500 to-fuchsia-700",
  yellow: "from-yellow-300 to-orange-500",
};

function BadgeGroupSection({
  group,
  earnedBadgeKeys,
  badgeImageByKey,
}: {
  group: (typeof BADGE_GROUPS)[number];
  earnedBadgeKeys: Set<string>;
  badgeImageByKey: Map<string, string>;
}) {
  const categoriesContent = (
    <div className="space-y-4">
      {group.categories.map((category) => (
        <section key={category.key} className="rounded-3xl border border-violet-200/70 bg-white/80 p-5 shadow-sm dark:border-violet-500/20 dark:bg-violet-950/25">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="text-sm font-extrabold uppercase tracking-wide text-slate-800 dark:text-slate-100">{category.title}</h4>
          </div>

          <div className={group.key === "community" ? "max-h-[340px] space-y-2 overflow-y-auto pr-1" : "space-y-2"}>
            {category.items.map((badge) => {
              const unlocked = earnedBadgeKeys.has(badge.key);
              const imageUrl = badgeImageByKey.get(badge.key);

              return (
                <div
                  key={badge.key}
                  className={`flex items-center justify-between rounded-2xl border p-3 transition ${
                    unlocked
                      ? "border-violet-200 bg-white dark:border-violet-400/20 dark:bg-violet-950/30"
                      : "border-slate-200/70 bg-slate-100/70 opacity-80 dark:border-violet-400/10 dark:bg-violet-950/15"
                  }`}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div className={`grid h-11 w-11 place-items-center overflow-hidden rounded-xl ${imageUrl ? "bg-transparent" : "bg-gradient-to-br from-orange-400 to-orange-600 text-white"}`}>
                      {imageUrl ? <img src={imageUrl} alt={badge.label} className="h-full w-full object-cover" /> : <Crown className="h-5 w-5" />}
                    </div>

                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-slate-900 dark:text-slate-100">{badge.label}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{badge.rule}</p>
                    </div>
                  </div>

                  {unlocked ? <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-500" /> : <Lock className="h-5 w-5 shrink-0 text-slate-400" />}
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );

  return categoriesContent;
}

export default function UserPanelGunluk() {
  const { user, loading } = useSupabaseSession();

  const summaryQuery = useQuery({
    queryKey: ["progress-summary", user?.id],
    enabled: Boolean(user?.id) && !loading,
    queryFn: fetchProgressSummary,
    staleTime: 8_000,
  });

  const timelineQuery = useQuery({
    queryKey: ["progress-timeline", user?.id],
    enabled: Boolean(user?.id) && !loading,
    queryFn: fetchProgressTimeline,
    staleTime: 8_000,
  });

  const summary = summaryQuery.data as
    | {
        totals?: { total_xp?: number };
        daily?: {
          breath_xp?: number;
          meditation_xp?: number;
          task_xp?: number;
          community_xp?: number;
        };
        phase?: {
          rank?: { current?: { label?: string }; next?: { label?: string } | null; progress?: number; xpToNext?: number };
          aura?: { key?: string; level?: number; label?: string; score?: number; progressPercent?: number };
          streak?: { current?: number };
          badges?: Array<{ key: string; label: string; description?: string; group?: "practice" | "community" | "balance"; imageUrl?: string }>;
        };
        rankCatalog?: RankCatalogItem[];
        auraCatalog?: Array<{ key: string; label: string; minScore: number; imageUrl?: string }>;
        badgeCatalog?: Array<{ key: string; label?: string; imageUrl?: string }>;
      }
    | null;

  const timeline = timelineQuery.data as
    | {
        items?: Array<{ id: string; label?: string; source?: string; occurredAt?: string; awardedXp?: number }>;
      }
    | null;

  const totalXp = Math.max(0, Number(summary?.totals?.total_xp ?? 0));
  const rankCatalogFromApi: RankCatalogItem[] = Array.isArray(summary?.rankCatalog) ? summary.rankCatalog : [];
  const rankCatalog: RankCatalogItem[] = rankCatalogFromApi.length ? rankCatalogFromApi : DEFAULT_RANKS;
  const earnedBadges = Array.isArray(summary?.phase?.badges) ? summary.phase.badges : [];
  const earnedBadgeKeys = useMemo(() => new Set(earnedBadges.map((badge) => badge.key)), [earnedBadges]);
  const auraScore = Math.max(0, Number(summary?.phase?.aura?.score ?? totalXp));

  const auraLevels = useMemo(() => {
    const fromApi = Array.isArray(summary?.auraCatalog) ? summary.auraCatalog : [];
    if (!fromApi.length) return AURA_LEVELS;

    return fromApi.map((aura) => {
      const fallback = AURA_LEVELS.find((item) => item.key === aura.key);
      return {
        key: aura.key,
        label: aura.label,
        minScore: Math.max(0, Number(aura.minScore ?? 0)),
        imageUrl: aura.imageUrl,
        accent: fallback?.accent ?? "slate",
      };
    });
  }, [summary?.auraCatalog]);

  const badgeImageByKey = useMemo(() => {
    const map = new Map<string, string>();
    const catalog = Array.isArray(summary?.badgeCatalog) ? summary.badgeCatalog : [];
    for (const item of catalog) {
      const key = String(item?.key ?? "").trim();
      const imageUrl = String(item?.imageUrl ?? "").trim();
      if (!key || !imageUrl) continue;
      map.set(key, imageUrl);
    }
    return map;
  }, [summary?.badgeCatalog]);

  const currentAuraIndex = useMemo(() => {
    const auraKey = String(summary?.phase?.aura?.key ?? "").trim();
    if (auraKey) {
      const byKeyIndex = auraLevels.findIndex((item) => item.key === auraKey);
      if (byKeyIndex >= 0) return byKeyIndex;
    }

    let current = 0;
    for (let i = 0; i < auraLevels.length; i += 1) {
      if (auraScore >= auraLevels[i].minScore) current = i;
    }
    return current;
  }, [auraLevels, auraScore, summary?.phase?.aura?.key]);

  const currentAura = auraLevels[currentAuraIndex] ?? AURA_LEVELS[0];
  const nextAura = auraLevels[currentAuraIndex + 1] ?? null;
  const auraFloor = currentAura.minScore;
  const auraCeil = nextAura?.minScore ?? currentAura.minScore + 1;
  const auraProgress = nextAura
    ? Math.max(0, Math.min(100, Math.round(((auraScore - auraFloor) / Math.max(1, auraCeil - auraFloor)) * 100)))
    : 100;

  const userName = String(user?.metadata?.full_name ?? user?.email ?? "Kullanıcı").trim();

  const heroBadges = useMemo(() => {
    const currentRankLabel = String(summary?.phase?.rank?.current?.label ?? "Tohum").trim() || "Tohum";
    const currentRank = rankCatalog.find((rank) => rank.label === currentRankLabel) ?? rankCatalog[0] ?? null;

    const items: Array<{ key: string; label: string; imageUrl?: string; type: "aura" | "rank" | "badge" }> = [];

    items.push({
      key: `aura:${currentAura.key}`,
      label: summary?.phase?.aura?.label ?? `${currentAura.label} Aura`,
      imageUrl: currentAura.imageUrl,
      type: "aura",
    });

    if (currentRank) {
      items.push({
        key: `rank:${currentRank.key}`,
        label: currentRank.label,
        imageUrl: currentRank.imageUrl,
        type: "rank",
      });
    }

    for (const badge of earnedBadges) {
      items.push({
        key: `badge:${badge.key}`,
        label: badge.label,
        imageUrl: badge.imageUrl || badgeImageByKey.get(badge.key),
        type: "badge",
      });
    }

    return items;
  }, [badgeImageByKey, currentAura.imageUrl, currentAura.key, currentAura.label, earnedBadges, rankCatalog, summary?.phase?.aura?.label, summary?.phase?.rank?.current?.label]);

  if (!user?.id && !loading) {
    return <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">Bu sayfayı görmek için giriş yapmalısın.</div>;
  }

  const auraCompleted = Math.max(1, currentAuraIndex + 1);

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 rounded-[28px] border border-violet-100/80 bg-gradient-to-br from-[#f7f2fb] via-[#f4f2fb] to-[#f0ecfb] p-4 shadow-sm dark:border-violet-500/15 dark:bg-gradient-to-b dark:from-[#130d20] dark:via-[#110a1d] dark:to-[#120c20] sm:p-6">
      <section className="rounded-3xl border border-violet-200/70 bg-white/80 p-6 text-center shadow-sm dark:border-violet-500/20 dark:bg-violet-950/25">
        <h1 className="text-2xl font-extrabold text-slate-900 dark:text-slate-100">Ruhsal Yolculuk Rozetleri</h1>

        <div className="mt-4 flex gap-4 overflow-x-auto pb-1">
          {heroBadges.map((badge) => {
            const isWhiteAura = badge.type === "aura" && currentAura.key === "beyaz";

            return (
              <div key={badge.key} className="flex min-w-[92px] shrink-0 flex-col items-center gap-2">
                <div
                  className={`grid h-16 w-16 place-items-center overflow-hidden rounded-full ${
                    badge.type === "aura"
                      ? isWhiteAura
                        ? "bg-gradient-to-br from-white via-slate-100 to-slate-200 ring-2 ring-violet-200 shadow-[0_0_22px_rgba(196,181,253,0.9)]"
                        : "bg-gradient-to-br from-blue-400 to-indigo-600 ring-2 ring-violet-200 shadow-[0_0_18px_rgba(124,58,237,0.35)]"
                      : badge.imageUrl
                        ? "bg-transparent"
                        : "bg-gradient-to-br from-orange-400 to-orange-600 text-white"
                  }`}
                >
                  {badge.type === "aura" ? (
                    badge.imageUrl ? <img src={badge.imageUrl} alt={badge.label} className="h-[72%] w-[72%] rounded-full object-cover" /> : <div className="h-[72%] w-[72%] rounded-full bg-white/80" />
                  ) : badge.imageUrl ? (
                    <img src={badge.imageUrl} alt={badge.label} className="h-full w-full object-cover" />
                  ) : (
                    <Crown className="h-6 w-6" />
                  )}
                </div>
                <span className="max-w-[92px] text-center text-xs font-bold leading-tight text-slate-700 dark:text-slate-200">{badge.label}</span>
              </div>
            );
          })}
        </div>

        <p className="mt-3 text-sm font-semibold text-violet-600 dark:text-violet-300">{summary?.phase?.aura?.label ?? `${currentAura.label} Aura Seviyesi`}</p>
        <span className="mt-2 inline-flex rounded-full border border-violet-200 bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700 dark:border-violet-500/30 dark:bg-violet-900/30 dark:text-violet-200">
          Toplam: {totalXp.toLocaleString("tr-TR")} Yolculuk Puanı
        </span>
      </section>

      <section className="rounded-3xl border border-[#e9dcf0] bg-gradient-to-r from-[#fff9ff] via-[#f9f5ff] to-[#f6f7ff] p-5 shadow-sm dark:border-violet-500/20 dark:bg-violet-950/25">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Faz 2 • Aura & Unvan</div>
            <div className="mt-1 text-3xl font-black text-slate-900 dark:text-slate-100">{summary?.phase?.rank?.current?.label ?? "Tohum"}</div>
            <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              Aura: {summary?.phase?.aura?.label ?? `${currentAura.label} Aura`} • Seri: {summary?.phase?.streak?.current ?? 0} gün
            </div>
          </div>
          <div className="rounded-2xl bg-[#f3ebf6] px-4 py-2.5 text-right text-sm font-semibold text-slate-700 dark:bg-violet-900/35 dark:text-slate-200">
            Toplam XP: {totalXp.toLocaleString("tr-TR")}
          </div>
        </div>

        <div className="mt-4 space-y-3">
          <div>
            <div className="mb-1 flex items-center justify-between text-xs text-slate-600 dark:text-slate-300">
              <span>Unvan ilerlemesi</span>
              <span>%{Math.max(0, Math.min(100, Number(summary?.phase?.rank?.progress ?? 0)))}</span>
            </div>
            <div className="h-2 rounded-full bg-slate-200/80 dark:bg-slate-700/70">
              <div className="h-full rounded-full bg-[#8e6cf2] transition-all" style={{ width: `${Math.max(0, Math.min(100, Number(summary?.phase?.rank?.progress ?? 0)))}%` }} />
            </div>
            {summary?.phase?.rank?.next ? (
              <div className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                Sonraki unvan: {summary.phase.rank.next.label} • {Math.max(0, Number(summary.phase.rank.xpToNext ?? 0))} XP kaldı
              </div>
            ) : (
              <div className="mt-1 text-xs text-slate-600 dark:text-slate-300">Maksimum unvana ulaştın.</div>
            )}
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between text-xs text-slate-600 dark:text-slate-300">
              <span>Aura ilerlemesi</span>
              <span>%{Math.max(0, Math.min(100, Number(summary?.phase?.aura?.progressPercent ?? auraProgress)))}</span>
            </div>
            <div className="h-2 rounded-full bg-slate-200/80 dark:bg-slate-700/70">
              <div className="h-full rounded-full bg-[#ef7a3d] transition-all" style={{ width: `${Math.max(0, Math.min(100, Number(summary?.phase?.aura?.progressPercent ?? auraProgress)))}%` }} />
            </div>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {earnedBadges.length ? (
            earnedBadges.slice(0, 4).map((badge) => (
              <span key={badge.key} className="rounded-full border border-[#dbc6f1] bg-white px-3 py-1 text-xs font-semibold text-[#5f3f86] dark:border-violet-500/30 dark:bg-violet-900/30 dark:text-violet-100">
                {badge.label}
              </span>
            ))
          ) : (
            <span className="text-xs text-slate-600 dark:text-slate-300">Rozetler ilk milestone sonrası burada görünecek.</span>
          )}
        </div>

        <div className="mt-4 grid gap-2 rounded-2xl border border-[#e7d7ef] bg-white/70 p-3 text-xs text-slate-700 dark:border-violet-500/25 dark:bg-violet-950/30 dark:text-slate-200 sm:grid-cols-2">
          <div className="flex items-center justify-between rounded-lg bg-white/80 px-2.5 py-2 dark:bg-violet-900/30">
            <span>Nefes XP</span>
            <span className="font-semibold">{Math.min(20, Math.max(0, Number(summary?.daily?.breath_xp ?? 0)))}/20</span>
          </div>
          <div className="flex items-center justify-between rounded-lg bg-white/80 px-2.5 py-2 dark:bg-violet-900/30">
            <span>Meditasyon XP</span>
            <span className="font-semibold">{Math.min(25, Math.max(0, Number(summary?.daily?.meditation_xp ?? 0)))}/25</span>
          </div>
          <div className="flex items-center justify-between rounded-lg bg-white/80 px-2.5 py-2 dark:bg-violet-900/30">
            <span>Görev XP</span>
            <span className="font-semibold">{Math.min(30, Math.max(0, Number(summary?.daily?.task_xp ?? 0)))}/30</span>
          </div>
          <div className="flex items-center justify-between rounded-lg bg-white/80 px-2.5 py-2 dark:bg-violet-900/30">
            <span>Topluluk XP</span>
            <span className="font-semibold">{Math.min(35, Math.max(0, Number(summary?.daily?.community_xp ?? 0)))}/35</span>
          </div>
        </div>
      </section>

      <section className="rounded-3xl border border-violet-200/70 bg-white/80 p-5 shadow-sm dark:border-violet-500/20 dark:bg-violet-950/25">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-extrabold text-slate-900 dark:text-slate-100">Aura Seviyeleri</h2>
          <span className="text-xs font-bold text-violet-600 dark:text-violet-300">{auraCompleted}/{Math.max(1, auraLevels.length)} Tamamlandı</span>
        </div>

        <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
          {auraLevels.map((aura, index) => {
            const isCurrent = index === currentAuraIndex;
            const unlocked = index <= currentAuraIndex;
            const isWhiteAura = aura.key === "beyaz";

            return (
              <div key={aura.key} className={`flex flex-col items-center gap-1.5 ${!unlocked ? "opacity-50" : ""}`}>
                <div
                  className={`grid ${isCurrent ? "h-16 w-16" : "h-12 w-12"} place-items-center rounded-full bg-gradient-to-br ${auraAccent[aura.accent as keyof typeof auraAccent]} ${
                    isCurrent ? "ring-2 ring-violet-300 shadow-[0_0_18px_rgba(124,58,237,0.35)] dark:ring-violet-400/50" : ""
                  } ${isWhiteAura ? "shadow-[0_0_22px_rgba(196,181,253,0.95)]" : ""}`}
                >
                  {aura.imageUrl ? <img src={aura.imageUrl} alt={aura.label} className="h-full w-full rounded-full object-cover" /> : <div className="h-[72%] w-[72%] rounded-full bg-white/80" />}
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wide ${isCurrent ? "text-violet-600 dark:text-violet-300" : "text-slate-500 dark:text-slate-400"}`}>{aura.label}</span>
              </div>
            );
          })}
        </div>
      </section>

      <section className="rounded-3xl border border-violet-200/70 bg-white/80 p-5 shadow-sm dark:border-violet-500/20 dark:bg-violet-950/25">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-extrabold text-slate-900 dark:text-slate-100">Ana Unvanlar (Yolculuk Puanı)</h2>
          <span className="text-xs font-bold text-violet-600 dark:text-violet-300">{summary?.phase?.rank?.current?.label ?? "Tohum"}</span>
        </div>

        <div className="max-h-[420px] space-y-2 overflow-y-auto pr-1">
          {rankCatalog.map((rank, idx) => {
            const isCurrent = rank.label === summary?.phase?.rank?.current?.label;
            const unlocked = totalXp >= rank.minXp;
            const nextMin = rankCatalog[idx + 1]?.minXp;
            const rangeText = nextMin ? `${rank.minXp.toLocaleString("tr-TR")} - ${(nextMin - 1).toLocaleString("tr-TR")} Puan` : `${rank.minXp.toLocaleString("tr-TR")}+ Puan`;

            return (
              <div
                key={rank.key}
                className={`flex items-center justify-between rounded-2xl border p-3 transition ${
                  isCurrent
                    ? "border-violet-300 bg-violet-100/70 shadow-[0_0_16px_rgba(139,92,246,0.2)] dark:border-violet-500/60 dark:bg-violet-900/35"
                    : unlocked
                      ? "border-slate-200 bg-white dark:border-violet-400/20 dark:bg-violet-950/30"
                      : "border-slate-200/70 bg-slate-100/70 opacity-70 dark:border-violet-400/10 dark:bg-violet-950/15"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`grid h-14 w-14 place-items-center overflow-hidden rounded-2xl ${rank.imageUrl ? "bg-transparent" : "bg-gradient-to-br from-orange-400 to-orange-600 text-white"}`}>
                    {rank.imageUrl ? <img src={rank.imageUrl} alt={rank.label} className="h-full w-full object-cover" /> : <Crown className="h-6 w-6" />}
                  </div>

                  <div>
                    <p className={`text-sm font-bold ${isCurrent ? "text-violet-700 dark:text-violet-200" : "text-slate-900 dark:text-slate-100"}`}>{rank.label}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{isCurrent ? "Güncel Unvan" : rangeText}</p>
                  </div>
                </div>

                {isCurrent ? (
                  <span className="rounded-full bg-violet-200/80 px-2 py-1 text-[10px] font-bold uppercase text-violet-700 dark:bg-violet-800/70 dark:text-violet-200">Şu Anki</span>
                ) : unlocked ? (
                  <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                ) : (
                  <Lock className="h-5 w-5 text-slate-400" />
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="space-y-4">
        {[BADGE_GROUPS[0], BADGE_GROUPS[1], BADGE_GROUPS[2]].map((group) => (
          <BadgeGroupSection key={group.key} group={group} earnedBadgeKeys={earnedBadgeKeys} badgeImageByKey={badgeImageByKey} />
        ))}
      </section>

      <Card className="rounded-3xl border border-violet-300/40 bg-gradient-to-br from-violet-100/80 to-violet-50 p-4 dark:border-violet-500/30 dark:from-violet-900/30 dark:to-violet-950/30">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-lg font-extrabold text-slate-900 dark:text-slate-100">Sıradaki Hedef</div>
            <div className="text-sm text-slate-600 dark:text-slate-300">
              {nextAura ? `${nextAura.label} seviyesine geçmek için ${Math.max(0, nextAura.minScore - auraScore).toLocaleString("tr-TR")} puan daha gerekli.` : "Maksimum aura seviyesine ulaştın."}
            </div>
          </div>

          <div className="relative h-14 w-14 shrink-0">
            <svg viewBox="0 0 36 36" className="h-full w-full">
              <path className="stroke-violet-200 dark:stroke-violet-500/30" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" strokeWidth="3" />
              <path className="stroke-violet-600 dark:stroke-violet-300" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" strokeWidth="3" strokeLinecap="round" strokeDasharray={`${auraProgress}, 100`} />
              <text x="18" y="20.35" textAnchor="middle" className="fill-violet-700 text-[8px] font-bold dark:fill-violet-200">%{auraProgress}</text>
            </svg>
          </div>
        </div>
      </Card>

      {Array.isArray(timeline?.items) && timeline.items.length ? (
        <Card className="rounded-3xl border border-violet-200/70 bg-white/80 p-4 shadow-sm dark:border-violet-500/20 dark:bg-violet-950/25">
          <h3 className="mb-2 text-sm font-bold text-slate-700 dark:text-slate-200">Son Aktiviteler</h3>
          <div className="space-y-2">
            {timeline.items.slice(0, 4).map((item) => (
              <div key={item.id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs dark:border-violet-500/20 dark:bg-violet-950/35">
                <span className="truncate text-slate-700 dark:text-slate-200">{item.label ?? "Aktivite"}</span>
                <span className="font-bold text-violet-700 dark:text-violet-200">+{Math.max(0, Number(item.awardedXp ?? 0))} XP</span>
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      <Card className="rounded-3xl border border-orange-200/70 bg-orange-50/80 p-4 dark:border-orange-700/40 dark:bg-orange-900/15">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-lg font-extrabold text-slate-900 dark:text-slate-100">Tebrikler, {userName.split(" ")[0] || "Yolcu"}!</div>
            <div className="text-sm text-slate-600 dark:text-slate-300">Kazanılan rozet: {earnedBadges.length}. Toplam puan: {totalXp.toLocaleString("tr-TR")}.</div>
          </div>
          <Award className="h-7 w-7 shrink-0 text-orange-500" />
        </div>
      </Card>
    </div>
  );
}
